import { ChangeDetectorRef, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { MatCard, MatCardContent, MatCardHeader, MatCardTitle } from '@angular/material/card';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { JsonPipe, NgClass, NgForOf, NgIf, NgTemplateOutlet } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TinyMCEModule } from '@sbd-doug-frontend/shared/lib/ui/tinymce/tinymce.module';
import { AccountUsersService } from '../../../../accountsusers/accountsusers.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { debounceTime, map, Subject, takeUntil } from 'rxjs';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { TasksetsService } from '../../../tasksets.service';
import { SuretyTeamWidgetService } from '../../../../dashboard/surety-team-widget/surety-team-widget.service';
import { AssignmentItem, SuretyTeamResponse } from '../../../../dashboard/surety-team-widget/surety-team.types';
import {
  MessageRecipient,
  TaskMessage,
  TaskMessageQueue,
  TaskSetResponse,
  TaskSetsListResponse,
  Task,
} from '../../../tasksets.types';
import { CathyService } from '../../../../../services/cathy.service';
import { ActivatedRoute, Router } from '@angular/router';
import { UserService } from '../../../../../core/user/user.service';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { MatTooltip } from '@angular/material/tooltip';
import {
  FileUploaderModalComponent,
} from '../../../../dashboard/open-quote-requests-widget/file-uploader-modal/file-uploader-modal.component';
import { FileUploadConfig } from '@sbd-doug-frontend/shared/lib/ui/file-upload/file-upload.component';
import { MatDialog } from '@angular/material/dialog';
import { CIPXScrollableContentComponent } from '@sbd-doug-frontend/shared/lib/ui';

@Component({
  selector: 'sbd-account-portal-frontend-send-message',
  standalone: true,
  templateUrl: './send-message.component.html',
  styleUrl: './send-message.component.scss',
  imports: [
    MatButton,
    MatInput,
    MatLabel,
    MatFormField,
    MatCardContent,
    MatCard,
    ReactiveFormsModule,
    TinyMCEModule,
    MatCheckbox,
    NgForOf,
    NgIf,
    MatIcon,
    NgClass,
    MatError,
    MatIconButton,
    MatTooltip,
    FileUploaderModalComponent,
    JsonPipe,
    NgTemplateOutlet,
    CIPXScrollableContentComponent,
  ],
})
export class SendMessageComponent implements OnInit {
  _formGroup: FormGroup;
  _formBuilder = inject(FormBuilder);
  _accountUsersService = inject(AccountUsersService);
  _userService = inject(UserService);
  _taskSetsService = inject(TasksetsService);
  _suretyTeamService = inject(SuretyTeamWidgetService);
  _unsubscribeAll: Subject<void> = new Subject<void>();
  _changeDetector = inject(ChangeDetectorRef);
  selectedRecipient = '';
  isScreenSmall = false;
  selectedRecipients: MessageRecipient[] = [];
  @Input() showTopBand = true;
  @Input() fullWidth = false;
  @Input() subject = '';
  @Input() showInlineButtons = true;
  @Input() inDrawer = false;
  @Output() cancelMessage: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Input() isReplying = false;
  @Input() task: Partial<Task> | null = null;
  _router = inject(Router);
  _cathyService = inject(CathyService);
  recipientList: MessageRecipient[] = [];
  _activatedRoute = inject(ActivatedRoute);
  _fuseMediaWatcherService = inject(FuseMediaWatcherService);
  isSubmitting = false;

  $messageRecipients = toObservable(this._suretyTeamService.recipientList).pipe(
    takeUntil(this._unsubscribeAll),
    map((messageRecipients: MessageRecipient[]) => {
      if (!this.isReplying && messageRecipients) {
        if (this._taskSetsService.taskMessage()?.new?.recipients?.length) {
          this.selectedRecipients = this._taskSetsService.taskMessage()?.new?.recipients;
        } else {
          this.selectedRecipients = messageRecipients.filter(t => t.type_code === 'accountmanager');
        }

        this._activatedRoute.queryParams.pipe(takeUntil(this._unsubscribeAll)).subscribe((params) => {
          const recipient = params['recipient'];
          if (recipient) {
            this.selectedRecipients = messageRecipients.filter(t => t.type_code === recipient);
          }
        });
        this._taskSetsService.taskMessage.set(
          {
            reply: { ...this._taskSetsService.taskMessage()?.reply },
            new: <TaskMessage>{
              task: {
                ...this._taskSetsService.taskMessage()?.new?.task,
              },
              recipients: this.selectedRecipients,
            },
          });
      }
      this._changeDetector.detectChanges();
    }),
  ).subscribe();

  $uiLookups = toObservable(this._accountUsersService.uiLookups).pipe(
    takeUntil(this._unsubscribeAll),
    map((lookups) => {
      if (lookups) {
        this._formGroup.patchValue({
          task_type_code: lookups.task_types.find(t => t.task_type_code === 'message').task_type_code,
        });
        this._changeDetector.detectChanges();
      }
    }),
  ).subscribe();


  constructor(public dialog: MatDialog) {
  }


  ngOnInit(): void {
    if (this.isReplying) {
      this._formGroup = this._formBuilder.group({
        task_label: [this.subject ? this.subject : this._taskSetsService.taskMessage()?.reply?.task?.task_label, [Validators.required]],
        task_description: [this._taskSetsService.taskMessage()?.reply?.task?.task_description, [Validators.required]],
        task_type_code: ['message'],
        assign_to: [this._taskSetsService.taskMessage()?.reply?.recipients],
        task_set_id: [this._taskSetsService.taskMessage()?.reply?.task?.task_set_id],
        replied_task_id: [this._taskSetsService.taskMessage()?.reply?.task?.task_id],
        is_replying: [this.isReplying],
        task_due_date: [new Date(new Date().setHours(new Date().getHours() + 1))],
      });

      this.recipientList = this._taskSetsService.taskMessage()?.reply.recipients?.sort((a, b) => a.scope_key === 'xbadminscope' ? -1 : b.scope_key === 'xbadminscope' ? 1 : 0);
      this.selectedRecipients = this._taskSetsService.taskMessage()?.reply.recipients;
      this.queuedFiles = this._taskSetsService.taskMessage()?.reply?.queuedFiles ?? [];
    } else {
      const newTaskFromSignal = this._taskSetsService.taskMessage()?.new?.task;
      this._formGroup = this._formBuilder.group({
        task_label: [
          this.subject ? this.subject : (this.task?.task_label ?? newTaskFromSignal?.task_label),
          [Validators.required],
        ],
        task_description: [this.task?.task_description ?? newTaskFromSignal?.task_description, [Validators.required]],
        task_type_code: ['message'],
        assign_to: [this.task?.assignees ?? newTaskFromSignal?.assignees],
        task_set_id: [this.task?.task_set_id ?? newTaskFromSignal?.task_set_id],
        replied_task_id: [newTaskFromSignal?.task_id],
        is_replying: [this.isReplying],
        task_due_date: [new Date(new Date().setHours(new Date().getHours() + 1))],
      });
      this.queuedFiles = this._taskSetsService.taskMessage()?.new?.queuedFiles ?? [];

      if (this.task) {
        this._taskSetsService.taskMessage.set({
          reply: { ...this._taskSetsService.taskMessage()?.reply },
          new: <TaskMessage>{
            task: {
              ...newTaskFromSignal,
              ...this.task,
            } as Task,
            recipients: this._taskSetsService.taskMessage()?.new?.recipients || this.selectedRecipients || [],
            queuedFiles: this.queuedFiles,
          },
        });
      }
    }

    this._formGroup.valueChanges.pipe(
      takeUntil(this._unsubscribeAll),
    ).subscribe((value) => {
      if (this.isReplying) {
        this._taskSetsService.taskMessage.set({
          new: { ...this._taskSetsService.taskMessage()?.new },
          reply:
            <TaskMessage>{
              task: {
                ...this._taskSetsService.taskMessage()?.reply?.task,
                task_label: value.task_label,
                task_description: value.task_description,
              },
              recipients: this.selectedRecipients,
              queuedFiles: this.queuedFiles,
            },
        });
      } else {
        this._taskSetsService.taskMessage.set({
          reply: { ...this._taskSetsService.taskMessage()?.reply },
          new:
            <TaskMessage>{
              task: {
                ...this._taskSetsService.taskMessage()?.new?.task,
                task_label: value.task_label,
                task_description: value.task_description,
              },
              recipients: this.selectedRecipients,
              queuedFiles: this.queuedFiles,
            },
        });
      }


    });

    // Subscribe to media changes
    this._fuseMediaWatcherService.onMediaChange$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({ matchingAliases }) => {
        // Check if the screen is small
        this.isScreenSmall = !matchingAliases.includes('md');
        if (this.isScreenSmall) {
        } else {
        }
      });

  }

  onTeamSelectChange($event: MatCheckboxChange, member: MessageRecipient) {
    const isChecked = $event.checked;
    if (isChecked) {
      const c = this.selectedRecipients.filter(t => t.scope_key === member.scope_key && t.type_code === member.type_code);
      if (!c.length) {
        this.selectedRecipients.push(member);
      }
    } else {
      const index = this.selectedRecipients.indexOf(member);
      if (index > -1) {
        this.selectedRecipients.splice(index, 1);
      }
    }
    this._formGroup.get('assign_to').setValue(this.selectedRecipients);
    this._changeDetector.detectChanges();
  }

  closePanel() {
    this._taskSetsService.showMessageEditor.set(false);
    if (this.isReplying) {
      this._taskSetsService.taskMessage.set(<TaskMessageQueue>{
        reply: null,
        new: this._taskSetsService.taskMessage()?.new,
      });
    } else {
      this._taskSetsService.displayNewMessagePlaceholder.set(false);
      this._taskSetsService.currentMenu.set('inbox');
      this._router.navigate(['/tasks']);
    }
    this.isReplying = false;
    this.cancelMessage.emit(false);
    this._taskSetsService.newMessage.set(false);
    this._changeDetector.detectChanges();
  }

  sendMessageAction() {
    let data = this._formGroup.getRawValue();

    if (this._formGroup.invalid) {
      return;
    }

    if (this.selectedRecipients.length === 0) {
      this._cathyService.error('Please select at least one recipient.');
      return;
    }

    if (this.isReplying) {
      const key = this._taskSetsService.taskMessage().reply.task.task_related_entity_key;
      const type = this._taskSetsService.taskMessage().reply.task.task_related_entity_type;
      data = {
        ...data,
        task_related_entity: { task_related_entity_key: key, task_related_entity_type: type },
      };
    } else {
      const key = this._taskSetsService.taskMessage().new?.task.task_related_entity_key;
      const type = this._taskSetsService.taskMessage().new?.task.task_related_entity_type;

      if(key && type){
        data = {
          ...data,
          task_related_entity: { task_related_entity_key: key, task_related_entity_type: type },
        };
      }

      const c: MessageRecipient[] = this.selectedRecipients;
/*      c.push({
        scope_key: 'cipxaccountuser',
        id: +this._userService.currentAccountUser().id,
        name: this._userService.currentAccountUser().name,
        initials: this._userService.currentAccountUser().initials,
        type: 'Account User',
        type_code: 'currentaccountuser',
      });*/
      data = {
        ...data,
        assign_to: c,
      };
    }

    //Patching Dups
    data.assign_to = data.assign_to.filter(
      (value, index, self) =>
        index ===
        self.findIndex(
          (t) => t.scope_key === value.scope_key && t.id === value.id,
        ),
    );


    if (this.queuedFiles.length > 0) {
      const formData = new FormData();

      // Append each file to FormData
      this.queuedFiles.forEach((file, index) => {
        formData.append(`task_files[${index}]`, file);
      });

      // Append the data properties as separate fields in FormData
      Object.keys(data).forEach((key) => {
        if (Array.isArray(data[key])) {
          // If the field is an array, we stringify it (or handle it as per your API requirements)
          formData.append(key, JSON.stringify(data[key]));
        } else {
          formData.append(key, data[key]);
        }
      });

      data = formData; // Replace the original data with the FormData object
    }

    this._formGroup.disable();
    this.isSubmitting = true;
    this._taskSetsService.sendMessage(data, this._router.url?.startsWith('/tasks')).subscribe((response: TaskSetResponse) => {
      if (response.success) {
        this._cathyService.success(response.message);

        if (this.inDrawer) {
          this.cancelMessage.emit(false);

          // If used inside a drawer and the current route is the Tasks module,
          // update the right panel by navigating to the created task set route as usual.
          if (this._router.url?.startsWith('/tasks')) {
            this._taskSetsService.getTaskSet(response.data.taskset.taskset.task_set_id).subscribe((r) => {
              if (r?.task_set_id) {
                this._taskSetsService.selectedTaskSetFromTaskSetList.set(r);
              }
            });
          }else if(this._router.url?.startsWith('/dashboard')){
            this._taskSetsService.getOpenItems().subscribe();
          }
        } else {
          this._taskSetsService.currentMenu.set('inbox');
          this._taskSetsService.selectedTaskSetFromTaskSetList.set(null);
          this._taskSetsService.showMessageEditor.set(false);
          if (!this.isReplying) {
            this._taskSetsService.displayNewMessagePlaceholder.set(false);
          }


          this._taskSetsService.getTaskSet(response.data.taskset.taskset.task_set_id).subscribe((r) => {
            if (r?.task_set_id) {
              this._taskSetsService.selectedTaskSetFromTaskSetList.set(r);
              this._router.navigate([`/tasks/task/${response.data.taskset.taskset.task_set_id}`], {
                skipLocationChange: false,
                onSameUrlNavigation: 'reload',
              });
            }
          });
        }


        //Go to message
        //this._formGroup.reset();
      } else {
        this.isSubmitting = false;
        this._formGroup.enable();
        this._cathyService.error(response.message);
      }
      this._changeDetector.detectChanges();
    });

  }

  markAsChecked(member: MessageRecipient) {
    return this.selectedRecipients?.find(t => t.scope_key === member.scope_key && t.type_code === member.type_code);
  }

  queuedFiles: File[] = [];

  getFileExtension(file: File): string {
    const nameParts = file.name.split('.');
    return nameParts.length > 1 ? nameParts.pop()?.toLowerCase() || '' : '';
  }

  openFileUploader() {
    const dialogData = {
      context: 'cipxtask',
      uploadConfig: <FileUploadConfig>{
        caption: '<p class="text-md">Drag Files Here or Click to Browse</p>',
        noButton: true,
        noIcon: false,
        horizontal: true,
      },
      defaultUploadConfig: <FileUploadConfig>{
        caption: '<p class="text-md">Drag Files Here or Click to Browse</p>',
        noButton: true,
        noIcon: false,
        horizontal: true,
      },
    };

    const dialogRef = this.dialog.open(FileUploaderModalComponent, {
      data: { ...dialogData },
      width: '20vw',
      height: '200pxvw',
      maxWidth: '80vw',
      maxHeight: '70vw',
      minHeight: '200px',
      minWidth: '300px',
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        (Array.from(result?.data?.queuedFiles) as File[]).forEach(f => {
          this.queuedFiles.push(f);
        });
        if (this.isReplying) {
          this._taskSetsService.taskMessage.set(
            {
              reply: { ...this._taskSetsService.taskMessage()?.reply, queuedFiles: this.queuedFiles },
              new: { ...this._taskSetsService.taskMessage().new },
            },
          );
        } else {
          this._taskSetsService.taskMessage.set(
            {
              reply: { ...this._taskSetsService.taskMessage()?.reply },
              new: { ...this._taskSetsService.taskMessage().new, queuedFiles: this.queuedFiles },
            },
          );
        }
        this._changeDetector.detectChanges();
      }
    });
    return;
  }

  deleteFile(index: number) {
    this.queuedFiles.splice(index, 1);
    this._changeDetector.detectChanges();
  }

  getSuretyTeamMemberById(id: number) {
    const m = this._suretyTeamService?.recipientList()?.filter(i => +i.id === +id && i.scope_key === 'xbadminscope');

    if (m?.length) {
      return m[0].type + ' - ';
    }
    return '';
  }
}
